import { useSyncExternalStore } from 'react';

/**
 * Colour theme (JIRA-36). Users choose System, Light or Dark; System follows the operating system. The choice is
 * remembered per browser in localStorage, and storage failures (private mode, blocked storage) only mean it is
 * not remembered. The resolved theme is set as data-theme on <html>, which tokens.css themes. Pages opened by
 * other platforms (the ticket viewer and intake popups) always follow the system, and printing is always light.
 * public/theme-init.js repeats the resolution before first paint; keep the two in step.
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'lj.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writePreference(preference: ThemePreference) {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* not persisted; the choice still applies until the page reloads */
  }
}

/** Embedded pages have no theme control, so a member's choice from the full app does not apply there. */
export function isEmbeddedPath(pathname: string) {
  return /^\/(viewer|intake)(\/|$)/.test(pathname);
}

export function resolveTheme(
  preference: ThemePreference,
  {
    systemDark,
    embedded = false,
    printing = false,
  }: { systemDark: boolean; embedded?: boolean; printing?: boolean },
): Theme {
  if (printing) return 'light';
  if (preference === 'system' || embedded) return systemDark ? 'dark' : 'light';
  return preference;
}

let preference: ThemePreference = readPreference();
let printHolds = 0;
const listeners = new Set<() => void>();

function systemDark() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

function currentTheme(): Theme {
  return resolveTheme(preference, {
    systemDark: systemDark(),
    embedded: isEmbeddedPath(window.location.pathname),
    printing: printHolds > 0,
  });
}

function apply() {
  const theme = currentTheme();
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', theme);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  writePreference(next);
  apply();
}

/** Forces the light theme until the returned release is called (printing, the PDF download). */
export function holdLightTheme() {
  printHolds++;
  apply();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    printHolds--;
    apply();
  };
}

let started = false;

/** Applies the theme and follows system and print changes. Called once at startup. */
export function startTheme() {
  apply();
  if (started) return;
  started = true;
  if (typeof window.matchMedia === 'function')
    window.matchMedia(DARK_QUERY).addEventListener('change', apply);
  let releasePrint: (() => void) | undefined;
  window.addEventListener('beforeprint', () => {
    releasePrint ??= holdLightTheme();
  });
  window.addEventListener('afterprint', () => {
    releasePrint?.();
    releasePrint = undefined;
  });
  // Another tab changed the choice.
  window.addEventListener('storage', (e) => {
    if (e.key !== THEME_STORAGE_KEY && e.key !== null) return;
    preference = readPreference();
    apply();
  });
}

/** The theme on screen now. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, currentTheme);
}

export function useThemePreference(): [ThemePreference, (next: ThemePreference) => void] {
  return [useSyncExternalStore(subscribe, () => preference), setThemePreference];
}
